/* eslint-disable fp/no-mutation */
/* eslint-disable prefer-destructuring */
import { ReactElement, useEffect, useRef, useState } from 'react'

import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import MuxPlayer from '@mux/mux-player-react'
import { throttle } from 'lodash'
import axios from 'src/utilities/http-client'
import { selectRoles, selectUserID } from 'src/v3/settings/settings.slice'
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Snackbar,
} from '@mui/material'
import { useVttSubtitles } from 'src/v3/utilities/hooks/use-vtt-subtitles'
import {
  selectClassID,
  selectCurrentStep,
  selectStreamItems,
  stepCompleted,
  StreamStepVideo,
  selectProgressItem,
  videoProgressMade,
  fetchProgress,
} from '../classroom.slice'

export default (): ReactElement => {
  let dispatch = useAppDispatch()
  let classID = useAppSelector(selectClassID)
  let userID = useAppSelector(selectUserID)
  let streamItems = useAppSelector(selectStreamItems)
  let currentStep = useAppSelector(selectCurrentStep)
  let video = streamItems[currentStep] as StreamStepVideo
  let progressItem = useAppSelector(selectProgressItem(currentStep))
  let roles = useAppSelector(selectRoles)
  let isAdmin = roles.includes('admin')
  let isSpecialAccess =
    userID === 'a7c45cd4-6118-491a-98d6-0b340ee6e954' ||
    userID === '91e3b1e3-8334-4d0e-a4f1-207c0e7598a2'
  let isCompleted = progressItem?.status === 'completed'
  let canSkip = isAdmin || isSpecialAccess || isCompleted
  let [warning, setWarning] = useState<'none' | 'speed' | 'seek'>('none')

  // Multi-tab cheating detection state
  let [isMultiTabDialogOpen, setIsMultiTabDialogOpen] = useState(false)
  let [isCountdownActive, setIsCountdownActive] = useState(false)
  let [countdownSeconds, setCountdownSeconds] = useState(30)

  // Insufficient progress detection state
  let [isInsufficientProgressDialogOpen, setIsInsufficientProgressDialogOpen] =
    useState(false)

  let isPlayerInteractionBlocked =
    isMultiTabDialogOpen ||
    isCountdownActive ||
    isInsufficientProgressDialogOpen

  // Track the last valid position and the max position ever reached
  let [lastValidPosition, setLastValidPosition] = useState(0)
  let [maxWatchedPosition, setMaxWatchedPosition] = useState(
    progressItem?.progress || 0,
  )
  let playerRef = useRef<any>(null)

  useEffect(() => {
    // Reset tracking when moving to a different video step
    setLastValidPosition(0)
    setMaxWatchedPosition(progressItem?.progress || 0)
  }, [video?.id])
  // Keep current video.id in a ref so the throttled function always uses the latest value
  let videoIdRef = useRef(video?.id)
  videoIdRef.current = video?.id

  // Load VTT subtitles if available
  let { vttUrl } = useVttSubtitles(video?.id)

  // Multi-tab cheating detection handlers
  let handleMultiTabConflictDetected = () => {
    if (playerRef.current) {
      playerRef.current.pause()
    }
    setIsMultiTabDialogOpen(true)
  }

  let handleConfirmContinuePlayback = () => {
    setIsMultiTabDialogOpen(false)
    setCountdownSeconds(30)
    setIsCountdownActive(true)
  }

  let handleInsufficientProgressDetected = () => {
    if (playerRef.current) {
      playerRef.current.pause()
    }
    setIsInsufficientProgressDialogOpen(true)
  }

  let handleAcknowledgeInsufficientProgress = () => {
    setIsInsufficientProgressDialogOpen(false)
    // Reset local tracking state
    setLastValidPosition(0)
    setMaxWatchedPosition(0)
    // Re-initialize stream state with latest progress from server
    dispatch(fetchProgress(classID))
  }

  // Countdown effect - manages the 30-second cooldown period
  useEffect(() => {
    if (!isCountdownActive) {
      return undefined
    }

    let intervalId = setInterval(() => {
      setCountdownSeconds((prev) => {
        if (prev <= 1) {
          clearInterval(intervalId)
          setIsCountdownActive(false)
          if (playerRef.current) {
            playerRef.current.play()
          }
          return 30
        }
        return prev - 1
      })
    }, 1000)

    return () => clearInterval(intervalId)
  }, [isCountdownActive])

  let postProgress = (elapsedTime: number) => {
    axios
      .post('/api/v2/classroom/stream-event/video-progress', {
        userID,
        progress: elapsedTime,
        classID,
        type: 'video',
        videoId: videoIdRef.current,
      })
      .catch((error) => {
        if (error.response?.status === 409) {
          handleMultiTabConflictDetected()
        }
      })
  }

  let handleVideoComplete = (event: any) => {
    let player = event.target
    let videoDuration = player.duration
    let COMPLETION_THRESHOLD = 3 // Allow 3 seconds of grace
    if (!isCompleted) {
      // Only mark as complete if the last valid position was near the end
      if (lastValidPosition >= videoDuration - COMPLETION_THRESHOLD) {
        axios
          .post('/api/v2/classroom/stream-event/video', {
            userID,
            classID,
            type: 'video',
            videoId: video.id,
            status: 'completed',
          })
          .then(() => {
            dispatch(stepCompleted())
          })
          .catch((error) => {
            if (error.response?.status === 409) {
              handleInsufficientProgressDetected()
            }
          })
      } else {
        // If they tried to skip to the end, reset to last valid position
        player.currentTime = lastValidPosition
      }
    }
  }

  let throttledProgress = useRef(
    throttle(
      (time: number) => {
        let currentVideoId = videoIdRef.current
        if (currentVideoId) {
          dispatch(videoProgressMade({ id: currentVideoId, progress: time }))
          postProgress(time)
        }
      },
      1000,
      { trailing: false },
    ),
  ).current

  let handleTimeUpdate = (event: any) => {
    let player = event.target
    let currentTime = player.currentTime
    let epsilon = 0.1
    let hasMeaningfulProgress =
      (progressItem?.progress ?? 0) > 0 &&
      Math.abs(currentTime - (progressItem?.progress || 0)) < epsilon

    if (!canSkip) {
      // Allow natural playback progression, backward seeking, and seeking
      // forward up to the max position the user has previously watched
      if (
        hasMeaningfulProgress ||
        currentTime <= maxWatchedPosition ||
        currentTime - lastValidPosition < 1
      ) {
        setLastValidPosition(currentTime)
        setMaxWatchedPosition((prev) => Math.max(prev, currentTime))
        // This is a valid progression, post progress every 20 seconds
        let time = Math.floor(currentTime)
        if (time > 10 && time % 20 === 0) {
          throttledProgress(time)
        }
      } else {
        // If there's a forward jump beyond max watched, reset
        player.currentTime = lastValidPosition
        setWarning('seek')
      }
    }
  }

  let handleRateChange = (event: any) => {
    let player = event.target
    if (!canSkip) {
      if (player.playbackRate !== 1.0) {
        player.playbackRate = 1.0
        setWarning('speed')
      }
    }
  }

  return (
    <div className="relative w-full aspect-video">
      <style>
        {!canSkip &&
          `
          mux-player {
            --seek-forward-button: none;
            --playback-rate-button: none;
            --pip-button: none;
            // For now we're allowing seek bar so a user can seek backwards
            // we may remove seek bar for the final version
            // --time-range: none;
          }
        `}
        {isPlayerInteractionBlocked &&
          `
          mux-player {
            pointer-events: none;
            --controls: none;
          }
        `}
      </style>
      <MuxPlayer
        title={isAdmin ? `${video.name} (Admin Mode)` : video.name}
        ref={playerRef}
        src={video?.videoUrl.replace('mp4', 'm3u8')}
        accentColor="#f9ea4f"
        primaryColor="#6694E9"
        backwardSeekOffset={20}
        forwardSeekOffset={20}
        metadata={{
          video_id: video.id,
          video_title: video.name,
          viewer_user_id: userID,
        }}
        onTimeUpdate={handleTimeUpdate}
        onRateChange={handleRateChange}
        onEnded={handleVideoComplete}
        defaultHiddenCaptions={!vttUrl} // Only hide captions if we don't have a VTT file
        streamType="on-demand"
        startTime={progressItem?.progress || 0}
      >
        {vttUrl && (
          <track
            src={vttUrl}
            kind="subtitles"
            label="English"
            srcLang="en"
            default
          />
        )}
      </MuxPlayer>

      {/* Overlay to block player interaction during countdown */}
      {isCountdownActive && (
        <div
          className="absolute inset-0 bg-black/50 flex items-center justify-center"
          style={{ zIndex: 10 }}
        >
          <div className="text-white text-xl font-semibold">Video Paused</div>
        </div>
      )}

      {/* Multi-tab detection confirmation dialog */}
      <Dialog
        open={isMultiTabDialogOpen}
        onClose={(_, reason) => {
          // Prevent closing by clicking outside or pressing escape - do nothing
          if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
            setIsMultiTabDialogOpen(false)
          }
        }}
        aria-labelledby="multi-tab-dialog-title"
        aria-describedby="multi-tab-dialog-description"
      >
        <DialogTitle id="multi-tab-dialog-title">
          Multiple Video Streams Detected
        </DialogTitle>
        <DialogContent id="multi-tab-dialog-description">
          We have detected video progress events from multiple courses within a
          short time period. For compliance purposes, you can only make progress
          on one course at a time to ensure proper credit hour tracking.
          <br />
          <br />
          If you recently switched from one course to another, please wait a
          moment before continuing. This helps us maintain accurate records of
          your learning time.
          <br />
          <br />
          By clicking &quot;Continue&quot;, you confirm this is the course you
          want to watch. You will need to wait 30 seconds before playback can
          resume.
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleConfirmContinuePlayback}
            variant="contained"
            color="primary"
          >
            Continue
          </Button>
        </DialogActions>
      </Dialog>

      {/* Insufficient progress detection dialog */}
      <Dialog
        open={isInsufficientProgressDialogOpen}
        onClose={(_, reason) => {
          // Prevent closing by clicking outside or pressing escape - do nothing
          if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
            setIsInsufficientProgressDialogOpen(false)
          }
        }}
        aria-labelledby="insufficient-progress-dialog-title"
        aria-describedby="insufficient-progress-dialog-description"
      >
        <DialogTitle id="insufficient-progress-dialog-title">
          Video Cannot Be Completed
        </DialogTitle>
        <DialogContent id="insufficient-progress-dialog-description">
          This video cannot be marked as complete at this time due to
          insufficient evidence of video progress.
          <br />
          <br />
          For compliance purposes, our system tracks viewing patterns to ensure
          proper credit hour tracking. Please watch the video content to
          continue.
          <br />
          <br />
          By clicking &quot;Continue&quot;, you will be returned to your last
          verified progress point.
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleAcknowledgeInsufficientProgress}
            variant="contained"
            color="primary"
          >
            Continue
          </Button>
        </DialogActions>
      </Dialog>

      {/* Warning Snackbar for speed/seek violations */}
      <Snackbar
        open={warning !== 'none'}
        autoHideDuration={6000}
        onClose={() => setWarning('none')}
      >
        <Alert
          onClose={() => setWarning('none')}
          severity="warning"
          variant="filled"
          sx={{ width: '100%' }}
        >
          {warning === 'speed'
            ? 'Playback speed changes are not allowed'
            : 'Seeking forward is not allowed'}
        </Alert>
      </Snackbar>

      {/* Countdown Snackbar for multi-tab cooldown */}
      <Snackbar
        open={isCountdownActive}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="info" variant="filled" sx={{ width: '100%' }}>
          Resuming playback in {countdownSeconds} seconds...
        </Alert>
      </Snackbar>
    </div>
  )
}
